import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useParams, useLocation, Navigate, Outlet } from "react-router-dom";
import { supabase } from "./supabaseClient";
import { useAuth } from "./useAuth";
import { DARK, LIGHT, makeCss } from "./lib/theme";
import { dbLoadSession, dbLoadAdminScope } from "./lib/db";
import { HelpModal, WelcomeLUModal, SessionClosed } from "./components/common";
import { LoginScreen } from "./screens/LoginScreen";
import { TeacherLayout } from "./screens/TeacherLayout";
import { HomeScreen } from "./screens/HomeScreen";
import { TeacherSetup, SessionCreated } from "./screens/TeacherSetup";
import { GroupJoin } from "./screens/GroupJoin";
import { PlayScreen } from "./screens/PlayScreen";
import { GroupResults } from "./screens/GroupResults";
import { DashboardScreen } from "./screens/DashboardScreen";
import { LeaderboardScreen } from "./screens/LeaderboardScreen";
import { QuestionBankScreen } from "./screens/QuestionBankScreen";
import { CoursesScreen } from "./screens/CoursesScreen";
import { TeacherProfileScreen } from "./screens/TeacherProfileScreen";
import { CourseViewScreen } from "./screens/CourseViewScreen";
import { SuperAdminScreen } from "./screens/SuperAdminScreen";
import "./lib/net"; // side effects: server-time sync + online/offline listeners

// ─────────────────────────────────────────
//  ROOT
// ─────────────────────────────────────────
// ─── Wrapper components for route-based rendering ───

function TeacherLayoutRoute({ user, isSuperAdmin, canAdminister, lang, setLang, theme, setTheme, G, css, onCreateFresh, modalOpen }) {
  if (!user) return <Navigate to="/" replace />;
  return (
    <TeacherLayout user={user} isSuperAdmin={isSuperAdmin} canAdminister={canAdminister} lang={lang} setLang={setLang} theme={theme} setTheme={setTheme} G={G} css={css} onCreateFresh={onCreateFresh} modalOpen={modalOpen}>
      <Outlet />
    </TeacherLayout>
  );
}

function DashboardRoute(p) {
  const { dashId } = useParams();
  const { user, loading } = useAuth();
  return <DashboardScreen {...p} dashId={dashId} user={user} authLoading={loading} onBack={() => p.navigate("/")} onHelp={(tab) => p.openHelp('teacher', tab || 'how')} />;
}

function CourseViewRoute(p) {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    supabase.from("courses")
      .select(`id, name, program_id, programs (id, name, org_id, organisations (id, name))`)
      .eq("id", courseId).maybeSingle()
      .then(({ data }) => { setCourse(data); setLoading(false); });
  }, [courseId]);
  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: p.G.muted }}>Laddar…</div>;
  if (!course) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: p.G.red }}>Kursen hittades inte.</div>;
  return <CourseViewScreen {...p} course={course} onBack={() => p.navigate("/courses")} onCreateTRAT={c => { p.setPreselectedCourse(c); p.navigate("/create"); }} onDashboard={dashId => p.navigate(`/dashboard/${dashId}`)} />;
}

function LeaderboardRoute({ lang, theme, setTheme, G }) {
  const { leaderboardId } = useParams();
  return <LeaderboardScreen leaderboardId={leaderboardId} lang={lang} theme={theme} setTheme={setTheme} G={G} />;
}

function PlayRoute(p) {
  const location = useLocation();
  const navigate = useNavigate();
  // Can come from location.state (normal flow) or query params (deep link)
  const stateSession = location.state?.session;
  const stateGroup = location.state?.groupName;
  const params = new URLSearchParams(location.search);
  const sid = params.get("s");
  const grp = params.get("g");

  const [session, setSession] = useState(stateSession || null);
  const [groupName, setGroupName] = useState(stateGroup || (grp ? decodeURIComponent(grp) : null));
  const [loading, setLoading] = useState(!stateSession && !!sid);

  useEffect(() => {
    if (!stateSession && sid) {
      dbLoadSession(sid).then(s => { if (s) setSession(s); setLoading(false); });
    }
  }, [sid, stateSession]);

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", color: p.G.muted }}>Laddar…</div>;
  if (!session || !groupName) return <Navigate to="/" replace />;
  // Avslutad tRAT: inga frågor att visa, och eleverna skickas till startsidan
  // (replace, så att bakåtknappen inte leder tillbaka hit).
  const exit = () => navigate("/", { replace: true });
  if (session.sessionLocked) return <SessionClosed onExit={exit} lang={p.lang} G={p.G} css={p.css} />;
  return <PlayScreen {...p} onExit={exit} session={session} groupName={groupName} onDone={st => navigate(groupPath("/result", session.sessionId, groupName), { state: { states: st, session, groupName } })} onHelp={() => p.openHelp('student')} />;
}

// Sessionen och gruppen ligger i adressen, inte bara i historikens state, så
// att resultatsidan och skrapvyn klarar en omladdning.
function groupPath(path, sessionId, groupName) {
  return `${path}?s=${encodeURIComponent(sessionId)}&g=${encodeURIComponent(groupName)}`;
}

function ResultRoute(p) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const sid = params.get("s");
  const stateGroup = location.state?.groupName;
  const groupName = stateGroup || params.get("g");
  const [session, setSession] = useState(location.state?.session || null);
  const [loading, setLoading] = useState(!location.state?.session && !!sid);

  useEffect(() => {
    if (!location.state?.session && sid) {
      dbLoadSession(sid).then(s => { if (s) setSession(s); setLoading(false); });
    }
  }, [sid, location.state]);

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", color: p.G.muted }}>Laddar…</div>;
  if (!session || !groupName) return <Navigate to="/" replace />;
  // Avslutad tRAT: inga frågor att visa, och eleverna skickas till startsidan
  // (replace, så att bakåtknappen inte leder tillbaka hit).
  const exit = () => navigate("/", { replace: true });
  if (session.sessionLocked) return <SessionClosed onExit={exit} lang={p.lang} G={p.G} css={p.css} />;
  // Utan medskickat läge (omladdning) börjar sidan tom; GroupResults hämtar
  // gruppens svar från servern direkt.
  const states = location.state?.states
    || Array.from({ length: session.numQ }, () => ({ attempts: [], done: false, score: null }));
  return <GroupResults {...p} onExit={exit} states={states} session={session} groupName={groupName} onBack={() => navigate(groupPath("/play", session.sessionId, groupName), { state: { session, groupName } })} onHelp={() => p.openHelp('student')} />;
}

function SessionJoinRoute(p) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  // Carry the session/group in the URL (not only in history state) so a reload,
  // shared link or PWA restart lands back in the same group instead of "/".
  return <GroupJoin {...p} sessionId={sessionId} onJoin={(s, n) => navigate(`/play?s=${encodeURIComponent(s.sessionId)}&g=${encodeURIComponent(n)}`, { state: { session: s, groupName: n } })} onBack={() => navigate("/")} onHelp={() => p.openHelp('student')} />;
}

export default function App() {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("trat.lang") || "sv"; } catch { return "sv"; }
  });
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("trat.theme") || "light"; } catch { return "light"; }
  });
  useEffect(() => { try { localStorage.setItem("trat.theme", theme); } catch {} }, [theme]);
  useEffect(() => { try { localStorage.setItem("trat.lang", lang); } catch {} }, [lang]);
  const [createdSession, setCreatedSession] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  // Invite/recovery links land here authenticated. Only LU login is offered
  // now, so instead of asking for a password we tell them how to get back in.
  const [showSetPassword, setShowSetPassword] = useState(() => {
    try { return /type=(recovery|invite)/.test(window.location.hash); } catch { return false; }
  });
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setShowSetPassword(true);
    });
    return () => subscription.unsubscribe();
  }, []);
  const [helpModalTab, setHelpModalTab] = useState('what');
  // Studentskärmarna öppnar en kort studentvy i hjälpen, lärarskärmarna de
  // tre lärarflikarna. Samma modal, olika startpunkt.
  const [helpAudience, setHelpAudience] = useState('teacher');
  const openHelp = (audience, tab) => {
    setHelpAudience(audience);
    setHelpModalTab(tab || (audience === 'student' ? 'student' : 'what'));
    setShowHelpModal(true);
  };
  const { user, loading: authLoading } = useAuth();
  // null = unknown (loading), false/true = resolved — the /superadmin route
  // must not flash-redirect real superadmins while the role check is pending
  const [isSuperAdmin, setIsSuperAdmin] = useState(null);
  const [preselectedCourse, setPreselectedCourse] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Vem som når administrationen: superadmin, eller programadmin för sina
  // program. Kursansvariga har inget att administrera.
  const [canAdminister, setCanAdminister] = useState(null);
  useEffect(() => {
    if (!user) { setIsSuperAdmin(false); setCanAdminister(false); return; }
    dbLoadAdminScope(user.id).then(scope => {
      setIsSuperAdmin(scope.isSuperAdmin);
      setCanAdminister(scope.isSuperAdmin || scope.programIds.length > 0);
    });
  }, [user]);

  // Väntande behörigheter (lärare som lagts till innan de loggat in, se
  // migrationen pending_roles): koppla dem till kontot vid inloggning. Blev
  // något kopplat laddas sidan om en gång, så att kurserna syns direkt.
  useEffect(() => {
    if (!user) return;
    supabase.rpc("claim_pending_roles").then(({ data, error }) => {
      if (error || !(data > 0)) return;
      try {
        if (sessionStorage.getItem("trat.claimed") === user.id) return;
        sessionStorage.setItem("trat.claimed", user.id);
      } catch { /* utan sessionStorage: ladda om ändå */ }
      window.location.reload();
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle legacy query-param URLs (redirect to proper routes)
  useEffect(() => {
    if (location.pathname !== "/") return;
    const params = new URLSearchParams(location.search);
    const lb = params.get("lb");
    const dash = params.get("dash");
    const sid = params.get("s");
    const grp = params.get("g");
    if (lb) navigate(`/leaderboard/${lb}`, { replace: true });
    else if (dash) navigate(`/dashboard/${dash}`, { replace: true });
    else if (sid && grp) navigate(`/play?s=${sid}&g=${encodeURIComponent(grp)}`, { replace: true });
    else if (sid) navigate(`/session/${sid}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const G = theme === "dark" ? DARK : LIGHT;
  const css = makeCss(G);
  const shared = { lang, setLang, theme, setTheme, G, css, navigate, onHome: () => navigate("/"), setShowHelpModal, setHelpModalTab, openHelp, setPreselectedCourse, isSuperAdmin };
  // Entry points that start a tRAT "from scratch" clear any course preselected
  // from an earlier visit to a course page, so it doesn't silently stick.
  const createFresh = () => { setPreselectedCourse(null); navigate("/create"); };
  const teacherLayoutProps = { user, isSuperAdmin, canAdminister, lang, setLang, theme, setTheme, G, css, onCreateFresh: createFresh, modalOpen: showSetPassword || showHelpModal };

  // Wait for auth to resolve before rendering routes to avoid redirect flash
  if (authLoading) {
    return <div style={{ minHeight: "100vh", background: G.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: G.muted, fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>Laddar…</div>
    </div>;
  }

  return (
    <>
      <Routes>
        {/* Leaderboard — standalone, no layout */}
        <Route path="/leaderboard/:leaderboardId" element={<LeaderboardRoute lang={lang} theme={theme} setTheme={setTheme} G={G} />} />

        {/* Student routes — no layout */}
        <Route path="/session/:sessionId" element={<SessionJoinRoute {...shared} />} />
        <Route path="/play" element={<PlayRoute {...shared} />} />
        <Route path="/result" element={<ResultRoute {...shared} />} />

        {/* Teacher routes — inside TeacherLayout */}
        {user && (
          <Route element={<TeacherLayoutRoute {...teacherLayoutProps} />}>
            <Route path="/" element={<HomeScreen {...shared} onTeacher={createFresh} onGroup={c => navigate(`/session/${c}`)} onDashboard={c => navigate(`/dashboard/${c}`)} onHelp={(tab) => openHelp('teacher', tab || 'what')} />} />
            <Route path="/create" element={<TeacherSetup {...shared} onCreated={s => { setCreatedSession(s); navigate("/created"); }} onBack={() => navigate("/")} preselectedCourse={preselectedCourse} onHelp={(tab) => openHelp('teacher', tab || 'how')} />} />
            <Route path="/created" element={createdSession ? <SessionCreated {...shared} session={createdSession} onDashboard={() => navigate(`/dashboard/${createdSession.dashId}`)} onBack={() => navigate("/create")} /> : <Navigate to="/create" replace />} />
            <Route path="/courses" element={<CoursesScreen {...shared} onCourseView={c => navigate(`/courses/${c.id}`)} onBack={() => navigate("/")} />} />
            <Route path="/courses/:courseId" element={<CourseViewRoute {...shared} />} />
            <Route path="/questionbank" element={<QuestionBankScreen {...shared} onBack={() => navigate("/")} />} />
            <Route path="/profile" element={<TeacherProfileScreen {...shared} />} />
            <Route path="/superadmin" element={
              canAdminister === false
                ? <Navigate to="/" replace />
                : canAdminister
                  ? <SuperAdminScreen {...shared} onBack={() => navigate("/")} />
                  : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: G.muted }}>Laddar…</div>
            } />
            <Route path="/dashboard/:dashId" element={<DashboardRoute {...shared} />} />
          </Route>
        )}

        {/* Dashboard via secret dash code — works without login (legacy/guest sessions) */}
        {!user && <Route path="/dashboard/:dashId" element={<DashboardRoute {...shared} />} />}

        {/* Public home — not logged in */}
        <Route path="/" element={<HomeScreen {...shared} onTeacher={createFresh} onGroup={c => navigate(`/session/${c}`)} onDashboard={c => navigate(`/dashboard/${c}`)} onHelp={(tab) => openHelp('teacher', tab || 'what')} />} />

        {/* Public login — fallthrough when not logged in and trying to reach a teacher route */}
        <Route path="/create" element={<LoginScreen lang={lang} G={G} css={css} />} />
        <Route path="/login" element={<LoginScreen lang={lang} G={G} css={css} />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <HelpModal show={showHelpModal} onClose={() => setShowHelpModal(false)} initialTab={helpModalTab} audience={helpAudience} lang={lang} G={G} css={css} />
      {user && showSetPassword && <WelcomeLUModal onClose={() => setShowSetPassword(false)} lang={lang} G={G} css={css} />}
    </>
  );
}
